import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { 
    addDays,
    isBefore, 
    isEqual, 
    isAfter, 
    endOfMonth, 
    differenceInDays, 
} from "date-fns";
import toast from 'react-hot-toast';
import { 
    useTheme, 
    Box, 
    Typography, 
    CircularProgress, 
} from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'state/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { fetchGroupedRosterDetailsByCrewCode } from 'state/api';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';


const RosterDetails = () => {
    useAuthCheck();
    const user = useSelector((state) => state.auth.user);
    const timeMode = useSelector((state) => state.auth.timemode);
    const params = useSelector((state) => state.auth.params);
    
    const theme = useTheme();

    const crewCode = user.crewCode;

    const { startDate, endDate } = useDateRangeContext();
    
    const [loading, setLoading] = useState(true);
    const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
    
    const INSTRUCTOR_SIMULATOR_CODES = params
        ?.find((item) => item.param === "INSTRUCTOR_SIMULATOR_CODES")
        ?.value.split(",")
        .map((x) => x.trim());
    
    const getAdjustedEndDate = useCallback(() => {
        const offset = 7;
        const today = new Date();
        const endDayOfMonth = endOfMonth(today);

        if (isBefore(endDate, endDayOfMonth) || isEqual(endDate, endDayOfMonth)) {
            return endDate;
        }

        if (isAfter(endDate, endDayOfMonth) && differenceInDays(endDate, today) > offset) {
            return endDayOfMonth;
        }

        return addDays(today, offset);
    }, [endDate]);

    useEffect(() => {
        const handleCrewRosters = async () => {
            if (!crewCode || !startDate || !endDate) return;
            setLoading(true);

            try {
                const toDate = getAdjustedEndDate();
                const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, toDate);

                setGroupedCrewRosters(result || []);
            } catch (error) {
                toast.error(error?.message || "Failed to fetch roster.");
            } finally {
                setLoading(false);
            }
        };
        
        handleCrewRosters();
    }, [crewCode, startDate, endDate, getAdjustedEndDate]);

    const getDay = (flight, field) =>
        timeMode === "UTC" ? flight[`${field}Day`] : flight[`${field}BaseDay`];

    const getTime = (flight, field) =>
        timeMode === "UTC" ? flight[field] : flight[`${field}Base`];

    const calculateFigures = (obj) => {
        let totalBlockMinutes = 0;
        let totalSectors = 0;
        let totalSimulatorMinutes = 0;

        Object.values(obj || {}).forEach((rosters) => {
            rosters.forEach((r) => {
                if (r.blockMinutes && !r.pax && r.routeNo) {
                    totalBlockMinutes += r.blockMinutes;
                    totalSectors += 1;
                }
                if (r.blockMinutes && INSTRUCTOR_SIMULATOR_CODES?.includes(r.flightNo))
                    totalSimulatorMinutes += r.blockMinutes;
            });
        });

        return {
            totalBlockHours: (totalBlockMinutes / 60).toFixed(2),
            totalSectors,
            totalSimulatorHours: (totalSimulatorMinutes / 60).toFixed(2),
        };
    };

    const { totalBlockHours, totalSimulatorHours } = calculateFigures(groupedCrewRosters);



    return (
        <Box className="main-container-box">   
            <FlexBetween>
                <Header 
                    title="Crew Schedule" 
                    subtitle={`${loading ? 'Loading...' : 'List of Rosters'}`}
                /> 
                <Typography>
                    BH: {totalBlockHours}
                    {totalSimulatorHours > 0 && `, SH: ${totalSimulatorHours}`}
                </Typography>
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    <Box
                        height={LAYOUT_HEIGHT}  
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                    >                            
                        {Object.entries(groupedCrewRosters || {}).map(([key, flights]) => (
                            <Box
                                key={key}
                                p={1}
                                sx={{
                                    borderTop: `1px solid ${theme.palette.neutral.dark}`,
                                }}
                            >

                                {flights.map((flight, idx) => {
                                    const day =
                                        flight.routeNo && flight.gdBegDay
                                            ? getDay(flight, "gdBeg")
                                            : flight.atdDay
                                            ? getDay(flight, "atd")
                                            : flight.stdDay;

                                    const reporting =
                                        flight.routeNo && flight.gdBegDay
                                            ? getTime(flight, "gdBeg")
                                            : null;

                                    const debrief =
                                        flight.routeNo && flight.gdEndDay
                                            ? getTime(flight, "gdEnd")
                                            : null;

                                    const atd = getTime(flight, "atd");
                                    const ata = getTime(flight, "ata");

                                    return (
                                        <Box key={idx} mb={1}>
                                            <Typography variant="h6" color="primary">
                                                {flight.routeNo && day
                                                    ? `${day} | Route: ${flight.routeNo}`
                                                    : day}
                                            </Typography>

                                            {reporting && (
                                                <Typography variant="h6" px={1}>
                                                    Reporting time: {reporting}
                                                </Typography>
                                            )}

                                            {/* Flight Line */}
                                            <FlexBetween gap={1} px={1}>
                                                <Typography
                                                    width="30px"
                                                    color="primary"                                                    
                                                >
                                                    {flight.flightNo}
                                                </Typography>

                                                <Typography width="30px" textAlign="center">
                                                    {atd}
                                                </Typography>
                                                <Typography width="30px" textAlign="center">
                                                    {flight.dep}
                                                </Typography>
                                                <Typography width="30px" textAlign="center">
                                                    {flight.arr}
                                                </Typography>
                                                <Typography width="30px" textAlign="center">
                                                    {ata}
                                                </Typography>

                                                {/* Pax indicator */}
                                                <Typography width="10px" textAlign="center">
                                                    {flight.pax && "*"}
                                                </Typography>

                                                <Typography width="10px" textAlign="center">
                                                    {flight.trnDuty}
                                                </Typography>
                                            </FlexBetween>

                                            {debrief && (
                                                <Typography variant="h6" px={1}>
                                                    Debriefing time: {debrief}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>                   
                </Box>
            )}

            <ScrollToTop />
        </Box>
    );
};

export default RosterDetails;
